<template>
    <div v-if="reservation" class="pt-1">
        <b-row v-if="reservationEdit">
            <b-col>
                <responsive-button
                    class="mt-4"
                    :icon="['fas', 'plus']"
                    variant="outline-primary"
                    @click="showAddGuestModal"
                    :responsiveness="false"
                    >Add Guest</responsive-button
                >
            </b-col>
        </b-row>
        <validation-observer
            :initialValues="{ email: '', name: '' }"
            ref="add_guest_form"
            v-slot="{ meta, setFieldValue, errors, validate }"
        >
            <modal
                :id="addGuestModalId"
                title="Add Guest"
                ok-title="Add"
                cancel-title="Close"
                @ok.prevent="addGuest"
                :ok-disabled="!meta.valid"
                @shown="validate"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-row>
                    <b-col>
                        <validation-provider name="name" rules="required">
                            <form-input
                                v-model="name"
                                floatingLabel="Name"
                                :error="errors.name"
                                @input="(name) => setFieldValue('name', name)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="email" :rules="`email${isEmailRequired ? '|required' : ''}`">
                            <form-input
                                v-model="email"
                                :leftIcon="['fas', 'at']"
                                floatingLabel="Email"
                                :error="errors.email"
                                @input="(email) => setFieldValue('email', email)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select v-model="age" floating-label="Age" :options="ageOptions" />
                    </b-col>
                </b-row>
            </modal>
        </validation-observer>
        <grid
            ref="grid"
            class="guest-invoices-grid pt-2"
            overlayNoRowsTemplate="No guests have been added"
            :columnDefs="columnDefs"
            :rowData="reservation.guest_names"
            :headerHeight="45"
            :paginationPageSize="6"
            :pagination="true"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            :with-space="false"
        >
        </grid>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import range from 'lodash/range';
    import isNil from 'lodash/isNil';
    import isEmpty from 'lodash/isEmpty';
    import API from '../../../../../api.jsx';
    import Grid from '../../../../generic/grid/grid.vue';
    import Modal from '../../../../generic/modal/modal.vue';
    import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../../../generic/form-input/form-input.vue';
    import FormSelect from '../../../../generic/form-select/form-select.vue';
    import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
    import ActionCellRenderer from './action-cell-renderer.vue';
    import { GUEST_GUEST_NAMES_BY_ID_ENDPOINT, GUEST_GUEST_NAMES_ENDPOINT } from '../../../../../endpoints';
    import { formatResponseError } from '../../../../../utils/api';
    import { isMediumScreen } from '../../../../../utils/screens';

    export default {
        name: 'GuestInvoicesGuestsTab',
        components: {
            Grid,
            Modal,
            BusyModal,
            FormInput,
            FormSelect,
            ResponsiveButton,
            actionCellRenderer: ActionCellRenderer,
        },
        data() {
            return {
                addGuestModalId: 'add-guest-modal-id',
                loading: false,
                name: '',
                email: '',
                age: null,
            };
        },
        inject: ['showSuccessToast', 'showErrorToast'],
        computed: {
            ...mapGetters('guest', ['metadata']),
            ...mapGetters('guest', ['reservation']),
            reservationEdit() {
                return this.metadata?.permissions?.reservation_edit;
            },
            isEmailRequired() {
                return this.metadata?.subscriber?.guest_verification_enabled;
            },
            columnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Name',
                        pinned: 'left',
                        minWidth: 175,
                        width: isMediumScreen() ? undefined : 175,
                        autoWidthExclude: this.isMobile,
                        type: 'editableColumn',
                        cellClass: this.reservationEdit ? 'lx-editable-cell text-primary' : '',
                        editable: this.reservationEdit,
                        cellEditorParams: { select: true },
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(GUEST_GUEST_NAMES_BY_ID_ENDPOINT(data.id), { name: newValue });
                                    this.refreshReservation(() =>
                                        this.showSuccessToast({ text: 'Guest name successfully updated.' })
                                    );
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'name',
                                        defaultText: 'Could not update name.',
                                    });
                                    data.name = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.name = newValue;
                            return true;
                        },
                        valueParser: ({ data }) => data.name,
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                        type: 'editableColumn',
                        cellClass: this.reservationEdit ? 'lx-editable-cell text-primary' : '',
                        editable: this.reservationEdit,
                        cellEditorParams: {
                            select: true,
                            empty: true,
                            emptyValue: '',
                        },
                        cellRenderer: ({ value }) => (!value ? 'Not Assigned' : value),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(GUEST_GUEST_NAMES_BY_ID_ENDPOINT(data.id), { email: newValue });
                                    this.refreshReservation(() =>
                                        this.showSuccessToast({ text: 'Guest email successfully updated.' })
                                    );
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'email',
                                        defaultText: 'Could not update email.',
                                    });
                                    data.email = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.email = newValue;
                            return true;
                        },
                        minWidth: 250,
                        valueParser: ({ data }) => data.email,
                    },
                    {
                        field: 'age',
                        headerName: 'Age',
                        cellClass: this.reservationEdit ? 'lx-editable-cell text-primary' : '',
                        editable: this.reservationEdit,
                        cellEditor: 'selectCellEditor',
                        autoWidthExclude: true,
                        cellEditorParams: {
                            select: true,
                            empty: true,
                            emptyValue: null,
                            options: this.ageOptions,
                        },
                        cellRenderer: ({ value }) => (isNil(value) ? 'Not Set' : value),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.patch(GUEST_GUEST_NAMES_BY_ID_ENDPOINT(data.id), { age: newValue });
                                    this.refreshReservation(() =>
                                        this.showSuccessToast({ text: 'Guest age successfully updated.' })
                                    );
                                } catch (e) {
                                    this.fieldBasedErrorToast({
                                        error: e,
                                        field: 'age',
                                        defaultText: 'Could not update age.',
                                    });
                                    data.age = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.age = newValue;
                            return true;
                        },
                        valueParser: ({ data }) => data.age,
                    },
                    {
                        field: '',
                        headerName: '',
                        editable: false,
                        sortable: false,
                        minWidth: 63,
                        maxWidth: 63,
                        cellRendererParams: {
                            refreshReservation: this.refreshReservation,
                        },
                        cellStyle: { paddingRight: 0, paddingLeft: '1rem' },
                        cellRenderer: 'actionCellRenderer',
                        hide: !this.reservationEdit,
                    },
                ];
            },
            ageOptions() {
                return [{ value: null, text: 'Not Set' }].concat(
                    range(1, 100).map((age) => ({
                        value: age,
                        text: age,
                    }))
                );
            },
            isMobile() {
                const md = 768;
                return window.innerWidth < md || window.innerHeight < md;
            },
        },
        methods: {
            refreshReservation(callback) {
                this.$emit('refreshReservations', callback);
            },
            fieldBasedErrorToast({ error, field, defaultText }) {
                const { generic, fields } = formatResponseError(error);
                const errorText = fields[field] && !!fields[field].length ? fields[field][0] : generic;
                this.showErrorToast({
                    text: errorText || defaultText,
                });
            },
            async showAddGuestModal() {
                this.name = '';
                this.email = '';
                this.age = null;
                this.$bvModal.show(this.addGuestModalId);
            },
            async addGuest() {
                this.loading = true;
                try {
                    await API.post(GUEST_GUEST_NAMES_ENDPOINT(), {
                        meta_reservation: this.reservation.id,
                        name: this.name,
                        email: this.email,
                        age: this.age,
                    });
                    this.$bvModal.hide(this.addGuestModalId);
                    this.refreshReservation(() => this.showSuccessToast({ text: `Guest successfully added.` }));
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);
                    if (!isEmpty(fields)) {
                        this.$refs.add_guest_form.setErrors(fields);
                    }
                    this.showErrorToast({ text: generic || `Could not add guest.` });
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../guest-invoices-grid.scss';
</style>
